/**
 * OptionCard Component
 *
 * Individual option card with thumbnail preview for visual selection.
 * Used within OptionPickerDialog for shot types, lighting, lens, etc.
 *
 * Design specs from design.pen:
 * - 120px height, #2A2A2A fill, 4px corner radius
 * - Label at top with uppercase styling
 * - Golden border (#D4A84B) when selected
 * - Hover triggers preview panel update
 */

import { useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import type { IOptionCardProps } from '@/@types/photostudio';
import { getOptionThumbnailPath, getPlaceholderThumbnailPath } from '@/@types/photostudio';

export function OptionCard({
  option,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onHoverEnd,
  category,
}: IOptionCardProps) {
  const [imageError, setImageError] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const thumbnailPath = imageError
    ? getPlaceholderThumbnailPath(category)
    : getOptionThumbnailPath(category, option.value);

  const handleImageError = () => {
    setImageError(true);
  };

  const handleMouseEnter = () => {
    if (buttonRef.current) {
      onHover(buttonRef.current);
    }
  };

  return (
    <button
      ref={buttonRef}
      type="button"
      onClick={onSelect}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHoverEnd}
      className={cn(
        'relative flex flex-col rounded-lg overflow-hidden transition-all duration-150',
        'h-[120px] w-full bg-[var(--muted)]',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#D4A84B] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--background-card)]',
        isSelected && 'ring-2 ring-[#D4A84B]',
        isHovered && !isSelected && 'ring-2 ring-gray-400'
      )}
    >
      {/* Background thumbnail */}
      <div className="absolute inset-0">
        <img
          src={thumbnailPath}
          alt=""
          className="h-full w-full object-cover opacity-60"
          onError={handleImageError}
          loading="lazy"
        />
        {/* Gradient overlay for better text readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent to-black/40" />
      </div>

      {/* Label at bottom */}
      <div className="absolute bottom-0 left-0 right-0 z-10 p-2">
        <span
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            'text-white drop-shadow-md',
            'line-clamp-2'
          )}
          style={{ letterSpacing: '0.5px' }}
        >
          {option.label}
        </span>
      </div>

      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute bottom-2 right-2 z-10">
          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#D4A84B]">
            <svg
              className="h-3 w-3 text-black"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={3}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
        </div>
      )}
    </button>
  );
}
